import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Download, X, Sparkles, LayoutTemplate, SlidersHorizontal, Loader2, Check } from 'lucide-react';
import { generatePatientOverallSummary } from '../../redux/appointmentSlice';
import { serverReason } from './prescriptionHelpers';

// The 6 overall-summary templates (keys match the AI prompt rules) with a short
// example so the doctor can see how each one is laid out before choosing.
const TEMPLATES = [
    {
        key: 'narrative', label: 'Narrative',
        desc: 'One flowing clinical paragraph.',
        example: 'John Doe has been managed over four visits for recurrent lower-right dental pain that began roughly a week before the first consultation and worsened with hot and cold foods, with examination revealing gum swelling and tenderness, leading to a diagnosis of dental caries treated with a root canal, after which the patient remained stable at the most recent review.',
    },
    {
        key: 'soap', label: 'SOAP',
        desc: 'Subjective / Objective / Assessment / Plan.',
        example: 'Subjective: Recurrent lower-right tooth pain, worse with hot/cold.\nObjective: Gum swelling and tenderness around the tooth.\nAssessment: Dental caries with pulpitis.\nPlan: Root canal completed; review in 2 weeks.',
    },
    {
        key: 'timeline', label: 'Chronological timeline',
        desc: 'One dated entry per appointment.',
        example: '2026-07-31 — Root canal consultation; severe lower-right pain, gum swelling noted.\n2026-06-10 — Initial visit; pain for ~1 week, worse with hot/cold.',
    },
    {
        key: 'problem', label: 'Problem-oriented',
        desc: 'Grouped by condition.',
        example: 'Dental caries — first noted with hot/cold sensitivity, progressed to severe pain, managed with root canal.\nGum inflammation — localized swelling and tenderness, resolving post-treatment.',
    },
    {
        key: 'concise', label: 'Concise / Executive',
        desc: '4–6 key bullet points.',
        example: '- Recurrent lower-right tooth pain across 3 visits\n- Diagnosed dental caries with pulpitis\n- Root canal completed\n- Stable at last review',
    },
    {
        key: 'detailed', label: 'Detailed clinical report',
        desc: 'History / Findings / Diagnosis / Treatment / Follow-up.',
        example: 'History: One week of worsening lower-right dental pain.\nFindings: Gum swelling and tenderness.\nDiagnosis: Dental caries with pulpitis.\nTreatment: Root canal.\nFollow-up: Review in 2 weeks.',
    },
];

const SummaryDrawer = ({ isOpen, onClose, patientId, appointments = [], patientCode = null }) => {
    const dispatch = useDispatch();
    const [panel, setPanel] = useState(null); // 'template' | 'custom' | null
    const [template, setTemplate] = useState('narrative');
    const [filters, setFilters] = useState({ lastN: '', specialties: [], appointmentTypes: [], dateFrom: '', dateTo: '' });
    const [state, setState] = useState({ loading: false, summary: '', count: null, error: '' });

    // Filter options derived from the patient's appointments (completed only).
    const { specialtyOptions, typeOptions } = useMemo(() => {
        const completed = (appointments || []).filter((a) => /completed/i.test(a?.status || ''));
        const specs = new Set();
        const types = new Set();
        completed.forEach((a) => {
            if (a.specialtyName) specs.add(a.specialtyName);
            else if (a.specialtyTypeName) specs.add(a.specialtyTypeName);
            if (a.appointmentType) types.add(a.appointmentType);
        });
        return { specialtyOptions: [...specs], typeOptions: [...types] };
    }, [appointments]);

    const toggle = (key, val) => setFilters((f) => {
        const set = new Set(f[key]);
        set.has(val) ? set.delete(val) : set.add(val);
        return { ...f, [key]: [...set] };
    });

    // The logged-in user's id — for a doctor this is the doctorId (mirrors how the
    // app reads patientId). Sent so the backend scopes to this doctor's appts.
    const doctorId = typeof window !== 'undefined' ? localStorage.getItem('ID') : null;

    const runGenerate = async (useCustom) => {
        if (!patientId) { setState((s) => ({ ...s, error: 'No patient selected.' })); return; }
        const activeFilters = useCustom ? {
            lastN: filters.lastN ? Number(filters.lastN) : undefined,
            specialties: filters.specialties,
            appointmentTypes: filters.appointmentTypes,
            dateFrom: filters.dateFrom || undefined,
            dateTo: filters.dateTo || undefined,
        } : undefined;
        setState({ loading: true, summary: '', count: null, error: '' });
        try {
            const res = await dispatch(generatePatientOverallSummary({ patientId, doctorId, template, filters: activeFilters })).unwrap();
            setState({
                loading: false,
                summary: res?.summary || '',
                count: res?.count ?? 0,
                error: (!res?.summary && (res?.message || 'No summaries found for the selected filters.')) || '',
            });
        } catch (e) {
            // A refusal carries its own sentence (403 OVERALL_SUMMARY_DOCTOR_ONLY:
            // the summary is prepared for the patient's own doctor only) — show
            // it; the generic line is for a failure that says nothing.
            setState({ loading: false, summary: '', count: null, error: serverReason(e, 'Failed to generate summary. Please try again.') });
        }
    };

    const download = () => {
        if (!state.summary) return;
        const blob = new Blob([state.summary], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // The file name a person reads carries the hospital's patient code,
        // never the system patient id (no code = no number at all).
        a.download = patientCode ? `overall-summary-${patientCode}.txt` : 'overall-summary.txt';
        a.click();
        URL.revokeObjectURL(url);
    };

    const activeTemplate = TEMPLATES.find((tpl) => tpl.key === template);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/20 z-[999] backdrop-blur-sm"
                        onClick={onClose} data-testid="summary-drawer-overlay"
                    />
                    <motion.div
                        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed top-0 right-0 h-full w-[60%] max-w-[90vw] bg-white shadow-2xl z-[1000] flex flex-col"
                        data-testid="summary-drawer"
                    >
                        {/* Header */}
                        <div className="p-5 border-b border-gray-100 shrink-0">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-[#4ABDBD]/10 rounded-lg">
                                        <FileText className="text-[#4ABDBD]" size={22} />
                                    </div>
                                    <h2 className="text-lg font-bold text-gray-800">Overall patient summary</h2>
                                </div>
                                <div className="flex items-center gap-2">
                                    {/* 1. Template — icon only */}
                                    <button
                                        onClick={() => setPanel(panel === 'template' ? null : 'template')}
                                        title="Template"
                                        aria-label="Template"
                                        className={`flex items-center justify-center w-10 h-10 rounded-xl border transition-colors ${panel === 'template' ? 'bg-[#4ABDBD]/10 border-[#4ABDBD] text-[#2D8A8A]' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                                        data-testid="summary-drawer-template-button"
                                    >
                                        <LayoutTemplate size={18} />
                                    </button>
                                    {/* 2. Generate summary */}
                                    <button
                                        onClick={() => runGenerate(false)}
                                        disabled={state.loading}
                                        className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#6C63FF] to-[#5A52E0] text-white rounded-xl text-sm font-semibold hover:from-[#5A52E0] hover:to-[#4840C9] disabled:opacity-60"
                                        data-testid="summary-drawer-generate-button"
                                    >
                                        {state.loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                                        Generate Summary
                                    </button>
                                    {/* 3. Custom summary generate */}
                                    <button
                                        onClick={() => setPanel(panel === 'custom' ? null : 'custom')}
                                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${panel === 'custom' ? 'bg-[#4ABDBD]/10 border-[#4ABDBD] text-[#2D8A8A]' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                                        data-testid="summary-drawer-custom-button"
                                    >
                                        <SlidersHorizontal size={16} /> Custom Summary Generate
                                    </button>
                                    <button
                                        onClick={download}
                                        disabled={!state.summary}
                                        className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-xl text-sm font-semibold hover:from-[#3AA8A8] hover:to-[#2D8A8A] disabled:opacity-40"
                                        data-testid="summary-drawer-download-button"
                                    >
                                        <Download size={16} /> Download
                                    </button>
                                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="summary-drawer-close-button">
                                        <X className="text-gray-500" size={22} />
                                    </button>
                                </div>
                            </div>
                            {/* Active template chip */}
                            <div className="mt-2 text-xs text-gray-500">
                                Format: <span className="font-semibold text-[#2D8A8A]">{activeTemplate?.label}</span>
                                {' · Only your appointments with this patient are used.'}
                            </div>
                        </div>

                        {/* Template picker */}
                        {panel === 'template' && (
                            <div className="p-4 border-b border-gray-100 bg-gray-50/60 max-h-[46%] overflow-y-auto" data-testid="summary-drawer-template-panel">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {TEMPLATES.map((tpl) => (
                                        <button
                                            key={tpl.key}
                                            onClick={() => { setTemplate(tpl.key); }}
                                            className={`text-left p-3 rounded-xl border transition-colors ${template === tpl.key ? 'border-[#4ABDBD] bg-white ring-1 ring-[#4ABDBD]/40' : 'border-gray-200 bg-white hover:border-[#4ABDBD]/50'}`}
                                            data-testid={`summary-template-${tpl.key}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-bold text-gray-800">{tpl.label}</span>
                                                {template === tpl.key && <Check size={16} className="text-[#2D8A8A]" />}
                                            </div>
                                            <p className="text-[11px] text-gray-500 mb-1">{tpl.desc}</p>
                                            <pre className="text-[11px] text-gray-600 whitespace-pre-wrap font-sans bg-gray-50 rounded-lg p-2 leading-snug">{tpl.example}</pre>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Custom filters */}
                        {panel === 'custom' && (
                            <div className="p-4 border-b border-gray-100 bg-gray-50/60 max-h-[46%] overflow-y-auto space-y-3" data-testid="summary-drawer-custom-panel">
                                <div className="flex items-center gap-2">
                                    <label className="text-sm font-semibold text-gray-700 w-40">Last N appointments</label>
                                    <input
                                        type="number" min="1" placeholder="e.g. 10"
                                        value={filters.lastN}
                                        onChange={(e) => setFilters((f) => ({ ...f, lastN: e.target.value }))}
                                        className="w-28 px-3 py-1.5 rounded-lg border border-gray-200 text-sm"
                                        data-testid="summary-custom-lastn"
                                    />
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <label className="text-sm font-semibold text-gray-700 w-40">Date range</label>
                                    <input type="date" value={filters.dateFrom} onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm" />
                                    <span className="text-gray-400">→</span>
                                    <input type="date" value={filters.dateTo} onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm" />
                                </div>
                                {specialtyOptions.length > 0 && (
                                    <div>
                                        <p className="text-sm font-semibold text-gray-700 mb-1">Specialty</p>
                                        <div className="flex flex-wrap gap-2">
                                            {specialtyOptions.map((s) => (
                                                <button key={s} onClick={() => toggle('specialties', s)} className={`px-3 py-1 rounded-full text-xs border ${filters.specialties.includes(s) ? 'bg-[#4ABDBD] text-white border-[#4ABDBD]' : 'border-gray-300 text-gray-600'}`}>{s}</button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {typeOptions.length > 0 && (
                                    <div>
                                        <p className="text-sm font-semibold text-gray-700 mb-1">Appointment type</p>
                                        <div className="flex flex-wrap gap-2">
                                            {typeOptions.map((tp) => (
                                                <button key={tp} onClick={() => toggle('appointmentTypes', tp)} className={`px-3 py-1 rounded-full text-xs border ${filters.appointmentTypes.includes(tp) ? 'bg-[#4ABDBD] text-white border-[#4ABDBD]' : 'border-gray-300 text-gray-600'}`}>{tp}</button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <button
                                    onClick={() => runGenerate(true)}
                                    disabled={state.loading}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#6C63FF] to-[#5A52E0] text-white rounded-xl text-sm font-semibold disabled:opacity-60"
                                    data-testid="summary-custom-generate"
                                >
                                    {state.loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                                    Generate with these filters
                                </button>
                            </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6" data-testid="summary-drawer-content">
                            {state.loading ? (
                                <div className="flex items-center gap-2 text-amber-600 text-sm">
                                    <Loader2 size={18} className="animate-spin" /> Generating overall summary…
                                </div>
                            ) : state.summary ? (
                                <div>
                                    <p className="text-xs text-gray-400 mb-3">Based on {state.count} appointment{state.count === 1 ? '' : 's'}.</p>
                                    <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line" data-testid="summary-drawer-result">{state.summary}</div>
                                </div>
                            ) : state.error ? (
                                <p className="text-sm text-gray-500" data-testid="summary-drawer-empty">{state.error}</p>
                            ) : (
                                <div className="text-center text-gray-400 mt-10">
                                    <Sparkles className="mx-auto mb-3 text-[#4ABDBD]/50" size={32} />
                                    <p className="text-sm">Choose a <b>Template</b> (or set <b>Custom</b> filters), then press <b>Generate</b> to build one overall summary from this patient's completed appointments you handled.</p>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default SummaryDrawer;
